import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9rc63vnahb7382ovkcg';

const aboutBreadcrumb = [
  { label: '首页', href: '/' },
  { label: '关于我们' },
];

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      setSubmitting(false);
      return;
    }

    formData.delete('phone_alt');

    try {
      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg =
          parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(serverMsg || '提交失败，请稍后再试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 继母的朋友们漫画资讯站',
    description: '了解继母的朋友们漫画资讯站的品牌故事、口碑评价与联系方式',
    url: 'https://stepmother-friends-manga.com/about',
    inLanguage: 'zh-CN',
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />
      <header>
        <Navbar />
      </header>
      <main className="min-h-screen pt-16">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb items={aboutBreadcrumb} />
            <div className="mt-2">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                关于我们
              </h1>
              <p className="text-sm text-foreground-500 mt-2">
                了解我们的品牌故事与使命
              </p>
            </div>
          </div>
        </div>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
              <div className="lg:col-span-2">
                <div className="rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Warm%20cozy%20creative%20workspace%20with%20manga%20books%20and%20art%20supplies%20on%20a%20wooden%20desk%2C%20soft%20natural%20light%20from%20window%2C%20editorial%20and%20storytelling%20atmosphere%2C%20warm%20amber%20tones%2C%20creative%20studio%20interior%2C%20high%20detail%20photography&width=600&height=800&seq=about-team-01&orientation=portrait"
                    alt="继母的朋友们漫画资讯站团队"
                    title="继母的朋友们漫画资讯站 - 我们的团队"
                    className="w-full h-auto object-cover object-top"
                  />
                </div>
              </div>
              <div className="lg:col-span-3">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                  品牌介绍
                </h2>
                <div className="text-sm md:text-base text-foreground-700 leading-relaxed space-y-4">
                  <p>
                    继母的朋友们漫画资讯站成立于2024年，是国内最早专注于《继母的朋友们》这部热门韩漫的综合性资讯平台之一。我们致力于为广大漫画爱好者提供最全面、最及时、最深度的漫画相关内容。
                  </p>
                  <p>
                    我们的团队由一群热爱漫画文化的资深读者和专业内容创作者组成。每一个人都对漫画有着深厚的理解和独特的视角，这使得我们能够从多个维度为读者呈现《继母的朋友们》的精彩内容。
                  </p>
                  <p>
                    我们的使命是成为连接《继母的朋友们》漫画与读者之间的桥梁。通过专业的剧情解析、深入的角色分析、及时的更新资讯和实用的阅读指南，帮助每一位读者更好地理解和享受这部优秀作品。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
              口碑评价
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  name: '漫画爱好者小李',
                  role: '资深读者',
                  content: '这个网站对继母的朋友们漫画的分析非常深入，角色解析部分尤其精彩。作为一个追了两年多的老读者，在这里看到了很多我之前忽略的细节。强烈推荐！',
                  rating: 5,
                },
                {
                  name: '二次元达人',
                  role: '漫画博主',
                  content: '资讯更新非常及时，每次新章节出来都能第一时间看到详细解读。网站的排版也很舒服，阅读体验很好。已经成为我每周必逛的网站了。',
                  rating: 5,
                },
                {
                  name: '深夜读书人',
                  role: '忠实粉丝',
                  content: '最喜欢网站的剧情梳理板块，把复杂的剧情线理得很清楚。对于新入坑的读者来说，这是一个完美的入门指南。期待网站能持续更新更多内容！',
                  rating: 4,
                },
              ].map((review, index) => (
                <div
                  key={index}
                  className="bg-background-50 rounded-lg p-5 border border-background-200/70"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: review.rating }).map((_, i) => (
                      <span key={i} className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-500 text-sm"></i>
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    "{review.content}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center">
                      <i className="ri-user-line text-secondary-500"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{review.name}</p>
                      <p className="text-xs text-foreground-400">{review.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-2xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
              联系我们
            </h2>
            <div className="bg-background-100 rounded-lg p-6 md:p-8 border border-background-200/70">
              {formStatus === 'success' ? (
                <div className="text-center py-8">
                  <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-checkbox-circle-line text-4xl text-primary-500"></i>
                  </div>
                  <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-2">
                    提交成功！
                  </h3>
                  <p className="text-sm text-foreground-500">
                    感谢您的留言，我们会尽快回复您。
                  </p>
                  <button
                    className="mt-4 px-4 py-2 text-sm text-primary-500 hover:text-primary-600 cursor-pointer"
                    onClick={() => setFormStatus('idle')}
                  >
                    发送新消息
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form="">
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1">
                        姓名
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-3 py-2 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1">
                        邮箱
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-3 py-2 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1">
                        留言内容
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-3 py-2 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all resize-none"
                        placeholder="请输入您的留言内容（不超过500字）"
                      ></textarea>
                      <p className="text-xs text-foreground-400 mt-1">最多500字</p>
                    </div>
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="honeypot-field"
                    />
                    {formStatus === 'error' && formError && (
                      <div className="p-3 bg-primary-50 text-primary-700 text-sm rounded-md">
                        {formError}
                      </div>
                    )}
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full py-3 bg-primary-500 text-background-50 rounded-md font-medium text-sm hover:bg-primary-600 transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                    >
                      {submitting ? (
                        <span className="flex items-center justify-center gap-2">
                          <i className="ri-loader-4-line animate-spin"></i> 提交中...
                        </span>
                      ) : (
                        '发送留言'
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}